import { KCAL_PER_NUTRIENT_GRAM, MacroSplit, MacroSplitRanges } from "../entities/MealsPlan";

type MacroTargets = { protein: number; carbs: number; fat: number };
type Macro = keyof MacroTargets;
const adjustableMacro: Record<Macro, Macro> = { protein: "carbs", carbs: "fat", fat: "protein" };
const preservedMacro: Record<Macro, Macro> = { protein: "fat", carbs: "protein", fat: "carbs" };
const clampPercentage = (value: number) => Math.max(0, Math.min(100, value));

export function updateNutritionMacroSplit(current: MacroTargets, macro: Macro, value: number): MacroTargets {
  if (!Number.isFinite(value)) return current;
  const next = { ...current, [macro]: clampPercentage(value) };
  const preserved = preservedMacro[macro];
  next[preserved] = Math.min(clampPercentage(current[preserved]), 100 - next[macro]);
  next[adjustableMacro[macro]] = 100 - next[macro] - next[preserved];
  return next;
}

export function normalizeNutritionMacroSplit(targets?: Partial<MacroTargets> | null): MacroTargets {
  const protein = targets?.protein ?? 30;
  const carbs = targets?.carbs ?? 40;
  const fat = targets?.fat ?? 30;
  if (![protein, carbs, fat].every((value) => Number.isFinite(value) && value >= 0))
    return { protein: 30, carbs: 40, fat: 30 };
  const total = protein + carbs + fat;
  if (total === 0) return { protein: 30, carbs: 40, fat: 30 };
  const normalizedProtein = Math.round((protein / total) * 10_000) / 100;
  const normalizedCarbs = Math.min(Math.round((carbs / total) * 10_000) / 100, 100 - normalizedProtein);
  return { protein: normalizedProtein, carbs: normalizedCarbs, fat: 100 - normalizedProtein - normalizedCarbs };
}

type SavedMacroTargets = Partial<MacroTargets> & { macroUnit?: "percentage" | "grams" | null };

export function getMacroSplitPreset(macroSplit?: MacroSplit | null): MacroTargets {
  return { ...(MacroSplitRanges[macroSplit ?? MacroSplit.BALANCED] ?? MacroSplitRanges[MacroSplit.BALANCED]) };
}

// Slider state for a saved plan or template: the sliders hold percentages, so gram targets
// (which would be saved back as percentages) and missing values fall back to the split preset.
export function resolveMacroSliderTargets(
  macroSplit: MacroSplit | null | undefined,
  targets: SavedMacroTargets | null | undefined,
): MacroTargets {
  const preset = getMacroSplitPreset(macroSplit);
  if (!targets || targets.macroUnit === "grams") return preset;
  return {
    protein: targets.protein ?? preset.protein,
    carbs: targets.carbs ?? preset.carbs,
    fat: targets.fat ?? preset.fat,
  };
}

// The generator reads macros as calorie percentages, so gram targets are weighted by kcal per gram first.
export function toCaloriePercentageTargets(
  macroSplit: MacroSplit | null | undefined,
  targets: SavedMacroTargets | null | undefined,
): MacroTargets {
  if (!targets) return normalizeNutritionMacroSplit(getMacroSplitPreset(macroSplit));
  if (targets.macroUnit === "grams") {
    return normalizeNutritionMacroSplit({
      protein: (targets.protein ?? 0) * KCAL_PER_NUTRIENT_GRAM.protein,
      carbs: (targets.carbs ?? 0) * KCAL_PER_NUTRIENT_GRAM.carbs,
      fat: (targets.fat ?? 0) * KCAL_PER_NUTRIENT_GRAM.fat,
    });
  }
  return normalizeNutritionMacroSplit(targets);
}
